import React, { useEffect, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import EventIcon from '@material-ui/icons/Event';
import dayjs from 'dayjs';
import {
    LocalizationProvider,
    MobileDateTimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import { parseDateTimeInput } from '../../utils/parseDateTimeInput';

const DateTimePickerWithManualInput = ({
    value,
    onChange,
    format,
    locale,
    label,
    minDateTime,
    maxDateTime,
    disableFuture = false,
    size = 'small',
    variant = 'standard',
    margin,
    fullWidth = false,
    multiline = false,
    rows = 1,
    minWidth,
    className,
}) => {
    const { t } = useTranslation();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        if (value && value.isValid && value.isValid()) {
            setInputValue(value.locale(locale).format(format));
        } else {
            setInputValue('');
        }
    }, [value, format, locale]);

    useEffect(
        () => () => {
            setPickerOpen(false);
        },
        []
    );

    const isWithinAllowedRange = parsed => {
        if (!parsed || !parsed.isValid || !parsed.isValid()) {
            return false;
        }
        if (disableFuture && parsed.isAfter(dayjs())) {
            return false;
        }
        if (minDateTime && parsed.isBefore(minDateTime)) {
            return false;
        }
        if (maxDateTime && parsed.isAfter(maxDateTime)) {
            return false;
        }
        return true;
    };

    const commitInput = () => {
        const parsed = parseDateTimeInput(inputValue, format, locale);
        if (parsed) {
            if (!isWithinAllowedRange(parsed)) {
                alert(t('reports.common.invalidDateTime'));
                if (value && value.isValid && value.isValid()) {
                    setInputValue(value.locale(locale).format(format));
                } else {
                    setInputValue('');
                }
                return;
            }
            onChange(parsed);
            return;
        }
        if (inputValue.trim()) {
            alert(t('reports.common.invalidDateTime'));
        }
    };

    const handlePickerChange = newValue => {
        if (!newValue) {
            onChange(null);
            return;
        }
        onChange(newValue);
        setPickerOpen(false);
    };

    const wrapperStyle = {};
    if (minWidth) {
        wrapperStyle.minWidth = minWidth;
    }
    if (fullWidth) {
        wrapperStyle.width = '100%';
    }

    return (
        <div className={className} style={wrapperStyle}>
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={locale}
            >
                <TextField
                    margin={margin}
                    label={label}
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onBlur={commitInput}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !multiline) {
                            e.preventDefault();
                            commitInput();
                        }
                    }}
                    size={size}
                    variant={variant}
                    fullWidth={fullWidth}
                    multiline={multiline}
                    rows={multiline ? rows : undefined}
                    InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label={label}
                                onClick={() => setPickerOpen(true)}
                                size="small"
                                edge="end"
                            >
                                <EventIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <MobileDateTimePicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onOpen={() => setPickerOpen(true)}
                value={value}
                onChange={handlePickerChange}
                format={format}
                ampm={false}
                closeOnSelect
                minDateTime={minDateTime}
                maxDateTime={maxDateTime}
                disableFuture={disableFuture}
                slotProps={{
                    textField: { sx: { display: 'none' } },
                }}
            />
            </LocalizationProvider>
        </div>
    );
};

export default DateTimePickerWithManualInput;
